import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import '../../css/Dashboard.css';
import { useDashboardData } from '../SalesDashboard/DashboardDataContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const BookingStackedChart: React.FC = () => {
  const { data: dash } = useDashboardData();

  // Build last 7 dates (labels as DD Mon)
  const dates = useMemo(() => {
    const arr: Date[] = [];
    for (let i = 6; i >= 0; i--) { const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - i); arr.push(d); }
    return arr;
  }, []);
  const labels = useMemo(() => dates.map(d => d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })), [dates]);
  const ymdLocal = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const dateKeys = useMemo(() => dates.map(ymdLocal), [dates]);

  const chartData = useMemo(() => {
    // categories by product_type
    const types = ['hotel', 'flight', 'transfer', 'activity'];
    const bucketIndex = (iso?: string) => {
      if (!iso) return -1;
      const dt = new Date(iso); dt.setHours(0,0,0,0);
      for (let i = 0; i < dates.length; i++) {
        const day = dates[i];
        if (dt.getTime() === day.getTime()) return i;
      }
      return -1;
    };

    const sums: Record<string, number[]> = {};
    types.forEach(t => { sums[t] = Array(7).fill(0); });
    const base = (dash?.bookingsByDate || []).slice();
    // If backend provided pre-aggregated bookingsByDate, use it directly
    if (base.length > 0) {
      const sums: Record<string, number[]> = {};
      types.forEach(t => { sums[t] = Array(7).fill(0); });
      base.forEach((row:any) => {
        const idx = dateKeys.indexOf(row.date);
        if (idx === -1) return;
        const t = String(row.type || '').toLowerCase();
        if (!sums[t]) sums[t] = Array(7).fill(0);
        sums[t][idx] += Number(row.amount || 0);
      });
      const palette: Record<string, string> = { hotel: '#93c5fd', flight: '#fbbf24', transfer: '#86efac', activity: '#c4b5fd' };
      const datasets = Object.keys(sums).map(t => ({ label: t[0].toUpperCase() + t.slice(1), data: sums[t], backgroundColor: palette[t] || '#a7f3d0' }));
      return { labels, datasets };
    }

    (dash?.bookings || []).forEach((b:any) => {
      if (String(b.status || '').trim().toUpperCase() === 'CANCELLED') return;
      const idx = bucketIndex(b.created_at || b.updated_at || b.travel_start_date);
      if (idx === -1) return;
      const t = String(b.product_type || '').toLowerCase();
      const amount = Number(b.total_price || 0);
      if (!sums[t]) sums[t] = Array(7).fill(0);
      sums[t][idx] += amount;
    });
    const palette: Record<string, string> = {
      hotel: '#93c5fd',
      flight: '#fbbf24',
      transfer: '#86efac',
      activity: '#c4b5fd',
    };
    const datasets = Object.keys(sums).map(t => ({ label: t[0].toUpperCase() + t.slice(1), data: sums[t], backgroundColor: palette[t] || '#a7f3d0' }));
    return { labels, datasets };
  }, [dash, dates, labels, dateKeys]);
  const options = {
    responsive: true,
    plugins: { legend: { position: 'top' as const } },
    scales: { x: { stacked: true }, y: { stacked: true } }
  };

  return (
    <div className="card">
      <h4 className="card-title">Booking Amount by Day</h4>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default BookingStackedChart;
